import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import AdminService from "../../../api/services/adminService";
import { formatNotesForExport, formatNotesForTable } from "../../../utils/earningPlanNotes";
import { STAKING_WALLET_HINT, isStakingWallet } from "../../../utils/walletTypes";
import "../Home/Homepage.css";
import "../Trading/CurrencyPairManagementPage.css";
import "../Trading/AllOpenOrdersPage.css";
import "./StakingStakesPage.css";
import "./StakingSubscribeByAdmin.css";
import { imageUrl } from "../../../api/apiConfig/apiConfig";

const DEFAULT_PAGE_LIMIT = 20;
const USER_SEARCH_DEBOUNCE_MS = 350;
const USER_SEARCH_MIN_CHARS = 2;
const USER_SEARCH_LIMIT = 10;

const subscribeUserIdentifier = (user) => {
  if (!user) return "";
  return String(user.emailId || user.uuid || user.mobileNumber || "").trim();
};
const PKG_PAGE_LIMIT = 100;
const EXPORT_BATCH = 500;
const PAGE_LIMIT_OPTIONS = [20, 50, 100, 500, 1000];
const EXCEL_SHEET_TITLE = "subscribe by admin";

const WALLET_TYPES = [
  { value: "main", label: "Main" },
  { value: "spot", label: "Spot" },
  { value: "p2p", label: "P2P" },
  { value: "futures", label: "Futures" },
  { value: "swap", label: "Swap" },
  { value: "earning", label: "Earning" },
  { value: "staking", label: "Staking (package only)" },
];

const SUBSCRIBE_TYPES = [
  { value: "offline", label: "Offline" },
  { value: "by_user_wallet", label: "By user wallet" },
];

const sanitizeAmount = (v) =>
  String(v ?? "")
    .replace(/,/g, "")
    .trim();

const extractDecimal = (v) => {
  if (v == null) return null;
  if (typeof v === "object" && v.$numberDecimal != null) return v.$numberDecimal;
  if (typeof v === "number" && Number.isFinite(v)) return v;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const pkgCoinKey = (p) => {
  const cid = p.currency_id ?? p.currencyId ?? p.currency?._id ?? p.currency?.id;
  if (cid != null && cid !== "") return String(cid);
  const sym = p.currency ?? p.currency_short_name ?? p.short_name;
  return sym ? `sym:${String(sym)}` : null;
};

const pkgId = (p) => p._id || p.id;

const planCurrencyCode = (plan) =>
  String(plan?.currency ?? plan?.currency_short_name ?? plan?.short_name ?? "")
    .trim()
    .toUpperCase();

const isZilPlan = (plan) => planCurrencyCode(plan) === "ZILN";

const getUsdtValue = (row) => row?.usdt_value ?? row?.usdtValue ?? null;

async function fetchAllActivePackages() {
  const all = [];
  let page = 1;
  let totalPages = 1;
  do {
    const res = await AdminService.getEarningPackages({
      page,
      limit: PKG_PAGE_LIMIT,
      status: "ACTIVE",
    });
    const list = Array.isArray(res?.data) ? res.data : [];
    all.push(...list);
    totalPages = res?.pagination?.total_pages || 1;
    page += 1;
  } while (page <= totalPages && page <= 80);
  return all;
}

const StakingSubscribeByAdminPage = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [packages, setPackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(false);

  const [selectedCoinKey, setSelectedCoinKey] = useState("");
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [emailId, setEmailId] = useState("");
  const [investAmount, setInvestAmount] = useState("");
  const [walletType, setWalletType] = useState("main");
  const [subscribeType, setSubscribeType] = useState("offline");
  const [notes, setNotes] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [stakeCheckResult, setStakeCheckResult] = useState(null);
  const [stakeCheckLoading, setStakeCheckLoading] = useState(false);
  const [reinvestConfirmOpen, setReinvestConfirmOpen] = useState(false);
  const [usdtConvertAmount, setUsdtConvertAmount] = useState("");
  const [convertLoading, setConvertLoading] = useState(false);
  const [convertResult, setConvertResult] = useState(null);
  const [convertError, setConvertError] = useState("");
  const [modalStep, setModalStep] = useState("form");
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [userSearchError, setUserSearchError] = useState("");
  const [showUserSuggestions, setShowUserSuggestions] = useState(false);
  const [selectedSubscribeUser, setSelectedSubscribeUser] = useState(null);
  const userSearchRequestRef = useRef(0);
  const userSearchWrapRef = useRef(null);

  const [subscriptions, setSubscriptions] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [page, setPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(DEFAULT_PAGE_LIMIT);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(null);
  const [userSearch, setUserSearch] = useState("");
  const [debouncedUser, setDebouncedUser] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [exportLoading, setExportLoading] = useState(false);
  const [exportMessage, setExportMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    const t = setTimeout(() => setDebouncedUser(userSearch.trim()), 1000);
    return () => clearTimeout(t);
  }, [userSearch]);

  useEffect(() => {
    setPage(1);
  }, [debouncedUser, from, to, pageLimit]);

  const loadPackagesForModal = useCallback(async () => {
    setPackagesLoading(true);
    try {
      const list = await fetchAllActivePackages();
      setPackages(list);
    } catch (e) {
      console.error(e);
      setPackages([]);
    } finally {
      setPackagesLoading(false);
    }
  }, []);

  const resetZilConvert = () => {
    setUsdtConvertAmount("");
    setConvertResult(null);
    setConvertError("");
    setConvertLoading(false);
  };

  const resetUserSearch = () => {
    setUserSearchResults([]);
    setUserSearchLoading(false);
    setUserSearchError("");
    setShowUserSuggestions(false);
    setSelectedSubscribeUser(null);
    userSearchRequestRef.current += 1;
  };

  const openModal = () => {
    setModalOpen(true);
    setModalStep("form");
    setSubmitError("");
    setSelectedCoinKey("");
    setSelectedPlanId("");
    setEmailId("");
    setInvestAmount("");
    setWalletType("main");
    setSubscribeType("offline");
    setNotes("");
    setStakeCheckResult(null);
    setStakeCheckLoading(false);
    setReinvestConfirmOpen(false);
    resetZilConvert();
    resetUserSearch();
    loadPackagesForModal();
  };

  const closeModal = () => {
    if (submitLoading) return;
    setModalOpen(false);
    setModalStep("form");
    setSubmitError("");
    setStakeCheckResult(null);
    setStakeCheckLoading(false);
    setReinvestConfirmOpen(false);
    resetZilConvert();
    resetUserSearch();
  };

  const handleUserInputChange = (value) => {
    setEmailId(value);
    setSelectedSubscribeUser(null);
    setShowUserSuggestions(true);
    setUserSearchError("");
  };

  const selectSubscribeUser = (user) => {
    setEmailId(subscribeUserIdentifier(user) || user.label || "");
    setSelectedSubscribeUser(user);
    setShowUserSuggestions(false);
    setUserSearchResults([]);
    setUserSearchError("");
  };

  useEffect(() => {
    if (!modalOpen || modalStep !== "form") return;
    const q = emailId.trim();
    if (q.length < USER_SEARCH_MIN_CHARS) {
      setUserSearchResults([]);
      setUserSearchLoading(false);
      setUserSearchError("");
      return;
    }
    const handle = setTimeout(() => {
      const requestId = Date.now();
      userSearchRequestRef.current = requestId;
      setUserSearchLoading(true);
      setUserSearchError("");
      AdminService.searchEarningUsers({ q, limit: USER_SEARCH_LIMIT })
        .then((res) => {
          if (userSearchRequestRef.current !== requestId) return;
          if (res?.success) {
            setUserSearchResults(Array.isArray(res.data) ? res.data : []);
          } else {
            setUserSearchResults([]);
            setUserSearchError(res?.message || "Could not search users.");
          }
        })
        .catch((err) => {
          if (userSearchRequestRef.current !== requestId) return;
          setUserSearchResults([]);
          setUserSearchError(
            err?.response?.data?.message || err?.message || "Could not search users."
          );
        })
        .finally(() => {
          if (userSearchRequestRef.current === requestId) {
            setUserSearchLoading(false);
          }
        });
    }, USER_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [emailId, modalOpen, modalStep]);

  useEffect(() => {
    if (!modalOpen) return;
    const onDocClick = (e) => {
      if (userSearchWrapRef.current && !userSearchWrapRef.current.contains(e.target)) {
        setShowUserSuggestions(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [modalOpen]);

  useEffect(() => {
    const id = emailId.trim();
    if (!modalOpen || !id || id.length < 2 || !selectedPlanId) {
      setStakeCheckResult(null);
      setStakeCheckLoading(false);
      return;
    }
    setStakeCheckLoading(true);
    setStakeCheckResult(null);
    const timer = setTimeout(() => {
      AdminService.checkExistingStake({ emailId: id, planId: selectedPlanId })
        .then((res) => {
          if (res?.success) setStakeCheckResult(res);
          else setStakeCheckResult(null);
        })
        .catch(() => setStakeCheckResult(null))
        .finally(() => setStakeCheckLoading(false));
    }, 600);
    return () => { clearTimeout(timer); setStakeCheckLoading(false); };
  }, [emailId, selectedPlanId, modalOpen]);

  const backToEditForm = () => {
    if (submitLoading) return;
    setModalStep("form");
    setSubmitError("");
  };

  const coinOptions = useMemo(() => {
    const opts = [];
    const seen = new Set();
    for (const p of packages) {
      const key = pkgCoinKey(p);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      const code = p.currency ?? p.currency_short_name ?? p.short_name ?? "—";
      const full = p.currency_fullname;
      opts.push({
        key,
        label: full && full !== code ? `${code} — ${full}` : code,
      });
    }
    opts.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
    return opts;
  }, [packages]);

  const plansForCoin = useMemo(() => {
    if (!selectedCoinKey) return [];
    return packages.filter((p) => pkgCoinKey(p) === selectedCoinKey);
  }, [packages, selectedCoinKey]);

  const selectedPlan = useMemo(
    () => plansForCoin.find((p) => String(pkgId(p)) === String(selectedPlanId)),
    [plansForCoin, selectedPlanId]
  );

  useEffect(() => {
    if (!selectedCoinKey) {
      setSelectedPlanId("");
      return;
    }
    const stillOk = plansForCoin.some((p) => String(pkgId(p)) === String(selectedPlanId));
    if (!stillOk) setSelectedPlanId("");
  }, [selectedCoinKey, plansForCoin, selectedPlanId]);

  useEffect(() => {
    if (!isZilPlan(selectedPlan)) {
      resetZilConvert();
    }
  }, [selectedPlanId, selectedPlan]);

  const buildFilterParams = useCallback(
    (pageNum, limitNum) => {
      const params = { page: pageNum, limit: limitNum };
      if (debouncedUser) params.user = debouncedUser;
      if (from) {
        const d = new Date(from);
        if (!Number.isNaN(d.getTime())) params.from = d.toISOString();
      }
      if (to) {
        const d = new Date(to);
        if (!Number.isNaN(d.getTime())) params.to = d.toISOString();
      }
      return params;
    },
    [debouncedUser, from, to]
  );

  const buildListParams = useCallback(
    () => buildFilterParams(page, pageLimit),
    [buildFilterParams, page, pageLimit]
  );

  const refreshSubscriptions = useCallback(async () => {
    setListLoading(true);
    setListError("");
    try {
      const res = await AdminService.getEarningUserPlansAdminForUser(buildListParams());
      const list = Array.isArray(res?.data) ? res.data : [];
      setSubscriptions(list);
      setTotalPages(res?.pagination?.total_pages || 1);
      setTotalCount(typeof res?.pagination?.total === "number" ? res.pagination.total : null);
      if (res?.success === false && !list.length) {
        setListError(res?.message || "Failed to load subscriptions.");
      }
    } catch (e) {
      console.error(e);
      setSubscriptions([]);
      setListError("Failed to load subscriptions.");
    } finally {
      setListLoading(false);
    }
  }, [buildListParams]);

  useEffect(() => {
    refreshSubscriptions();
  }, [refreshSubscriptions]);

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  const formatNum = (n) =>
    n != null && !Number.isNaN(Number(n))
      ? Number(n).toLocaleString("en-IN", { maximumFractionDigits: 12 })
      : "—";

  const formatDec = (v) => {
    const x = extractDecimal(v);
    return x != null ? formatNum(x) : "—";
  };

  const formatPlain = (v) => {
    const x = extractDecimal(v);
    return x != null ? String(x) : "—";
  };

  const userFullName = (u) => {
    if (!u) return "—";
    const first = u.first_name ?? u.firstName;
    const last = u.last_name ?? u.lastName;
    const parts = [first, last].filter(Boolean);
    return parts.length ? parts.join(" ") : "—";
  };

  const userPhone = (u) =>
    u?.phone ?? u?.mobileNumber ?? u?.mobile_number ?? u?.phone_number ?? null;

  const planLabel = (plan, row) => {
    const c = row?.currency ?? plan?.currency ?? plan?.short_name ?? plan?.currency_short_name;
    const d = plan?.duration_days ?? row?.duration_days;
    const r = plan?.return_percentage;
    if (plan && c != null && d != null && r != null) {
      return `${c} · ${d}d · ${r}%`;
    }
    if (c != null && row?.duration_days != null && row?.expected_return != null) {
      return `${c} · ${row.duration_days}d subscription`;
    }
    return c || plan?._id || plan?.id || row?.plan_id || "—";
  };

  const walletFlow = (row) => {
    const fromW = row.wallet_type ?? row.walletType ?? "—";
    const toW = row.credited_wallet_type ?? row.creditedWalletType ?? "—";
    if (fromW === "—" && toW === "—") return "—";
    return `${fromW} → ${toW}`;
  };

  const formatDateExport = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return String(iso);
    }
  };

  const refundExportText = (row) => {
    const st = String(row.status ?? "").toUpperCase();
    if (st !== "CANCELLED") return "";
    const refundAmt = row.refund_amount ?? row.refundAmount;
    const feePct = row.cancel_fee_percent ?? row.cancelFeePercent;
    const cancelledAt = row.cancelled_at ?? row.cancelledAt;
    return [
      refundAmt != null ? `Refund ${formatDec(refundAmt)}` : null,
      feePct != null ? `Fee ${feePct}%` : null,
      cancelledAt ? formatDateExport(cancelledAt) : null,
    ]
      .filter(Boolean)
      .join(" · ");
  };

  const rowToExport = (row) => {
    const u = row.user;
    const plan = row.plan;
    const termDays = row.duration_days ?? row.durationDays;
    const invested = row.invested_amount ?? row.investedAmount ?? row.amount;
    const expected = row.expected_return ?? row.expectedReturn ?? row.expected_reward;
    const start = row.start_date ?? row.startDate;
    const end = row.end_date ?? row.endDate;
    const empty = (v) => (v == null || v === "—" ? "" : v);
    return {
      UUID: empty(u?.uuid),
      Name: empty(userFullName(u)),
      Email: empty(u?.email),
      Phone: empty(userPhone(u)),
      Plan: empty(planLabel(plan, row)),
      "Term (days)": termDays != null ? String(termDays) : "",
      Invested: formatDec(invested) === "—" ? "" : formatDec(invested),
      "USDT value": formatDec(getUsdtValue(row)) === "—" ? "" : formatDec(getUsdtValue(row)),
      Expected: formatDec(expected) === "—" ? "" : formatDec(expected),
      Wallet: walletFlow(row) === "—" ? "" : walletFlow(row),
      Status: row.status ?? "",
      Notes: formatNotesForExport(row),
      Start: formatDateExport(start),
      End: formatDateExport(end),
      "Refund / cancel": refundExportText(row),
    };
  };

  const fetchAllFilteredSubscriptions = async () => {
    let currentPage = 1;
    let lastPage = 1;
    const all = [];

    do {
      const res = await AdminService.getEarningUserPlansAdminForUser(
        buildFilterParams(currentPage, EXPORT_BATCH)
      );
      if (res?.success === false && !Array.isArray(res?.data)) {
        throw new Error(res?.message || "Failed to fetch data for export.");
      }
      const list = Array.isArray(res?.data) ? res.data : [];
      all.push(...list);
      lastPage = res?.pagination?.total_pages || 1;
      currentPage += 1;
    } while (currentPage <= lastPage);

    return all;
  };

  const handleExportExcel = async () => {
    setExportLoading(true);
    setExportMessage({ type: "", text: "" });
    try {
      const allRows = await fetchAllFilteredSubscriptions();
      if (!allRows.length) {
        setExportMessage({
          type: "error",
          text: "No subscriptions match your filters to export.",
        });
        return;
      }

      const exportData = allRows.map(rowToExport);
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, EXCEL_SHEET_TITLE);

      const dateStamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `subscribe_by_admin_${dateStamp}.xlsx`);

      setExportMessage({
        type: "success",
        text: `Exported ${allRows.length} row(s) to Excel.`,
      });
      setTimeout(() => setExportMessage({ type: "", text: "" }), 4000);
    } catch (e) {
      console.error(e);
      setExportMessage({
        type: "error",
        text: e?.message || "Failed to export Excel file.",
      });
    } finally {
      setExportLoading(false);
    }
  };

  const handlePageLimitChange = (e) => {
    setPageLimit(Number(e.target.value) || DEFAULT_PAGE_LIMIT);
    setPage(1);
  };

  const pageStart = subscriptions.length === 0 ? 0 : (page - 1) * pageLimit + 1;
  const pageEnd = subscriptions.length === 0 ? 0 : (page - 1) * pageLimit + subscriptions.length;

  const statusBadgeClass = (st) => {
    const u = String(st || "").toUpperCase();
    if (u === "ACTIVE") return "global";
    if (u === "COMPLETED") return "local";
    return "local";
  };

  const handleZilConvert = async () => {
    const usdtRaw = usdtConvertAmount.trim();
    const usdt_amount = Number(usdtRaw);
    if (!usdtRaw || !Number.isFinite(usdt_amount) || usdt_amount <= 0) {
      setConvertError("Enter a valid USDT amount greater than 0.");
      return;
    }
    setConvertLoading(true);
    setConvertError("");
    setConvertResult(null);
    try {
      const res = await AdminService.convertZilPricing({ usdt_amount });
      if (res?.success && res.data) {
        setConvertResult(res.data);
      } else {
        setConvertError(res?.message || "Conversion failed.");
      }
    } catch (err) {
      console.error(err);
      setConvertError(err?.response?.data?.message || err?.message || "Conversion failed.");
    } finally {
      setConvertLoading(false);
    }
  };

  const applyConvertedZilAmount = () => {
    if (!convertResult) return;
    const zil =
      convertResult.zil_amount ??
      convertResult.zilAmount ??
      convertResult.amount ??
      convertResult.converted_amount;
    if (zil == null || zil === "") {
      setConvertError("No ZILN amount in conversion response.");
      return;
    }
    setInvestAmount(sanitizeAmount(zil));
    setConvertError("");
  };

  const validateSubscribeForm = () => {
    if (!selectedPlanId) return "Select a staking plan.";
    if (!emailId.trim()) return "Enter user email, mobile number, or UUID.";
    if (!investAmount.trim()) return "Enter invest amount.";
    return null;
  };

  const handleFormContinue = (e) => {
    e.preventDefault();
    setSubmitError("");
    const err = validateSubscribeForm();
    if (err) {
      setSubmitError(err);
      return;
    }
    if (stakeCheckResult?.already_staked) {
      setReinvestConfirmOpen(true);
      return;
    }
    setModalStep("review");
  };

  const handleReinvestConfirm = () => {
    setReinvestConfirmOpen(false);
    setModalStep("review");
  };

  const handleConfirmSubscribe = async () => {
    setSubmitError("");
    const err = validateSubscribeForm();
    if (err) {
      setSubmitError(err);
      setModalStep("form");
      return;
    }
    const email = emailId.trim();
    const amtRaw = sanitizeAmount(investAmount);
    setSubmitLoading(true);
    try {
      const res = await AdminService.subscribeEarningPackageByAdminForUser({
        planId: String(selectedPlanId),
        investAmount: amtRaw,
        walletType,
        emailId: email,
        type: subscribeType,
        notes: notes.trim(),
      });
      if (res?.success) {
        const msg = res?.message || "Subscription succeeded.";
        closeModal();
        refreshSubscriptions();
        toast.success(msg);
      } else {
        setSubmitError(res?.message || "Subscription failed.");
      }
    } catch (err) {
      console.error(err);
      setSubmitError(err?.response?.data?.message || err?.message || "Request failed.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const minHint = selectedPlan
    ? `Min stake for this plan: ${formatDec(selectedPlan.min_amount)} ${selectedPlan.currency ?? selectedPlan.currency_short_name ?? ""}`.trim()
    : "";

  const planIconSrc = (() => {
    const path = selectedPlan?.icon_path ?? selectedPlan?.iconPath;
    if (!path) return null;
    if (String(path).startsWith("http")) return path;
    const base = String(imageUrl || "").replace(/\/$/, "");
    return `${base}/${String(path).replace(/^\//, "")}`;
  })();

  const step1Done = Boolean(selectedCoinKey);
  const step2Done = Boolean(selectedPlanId);
  const step3Done = Boolean(emailId.trim() && investAmount.trim());
  const isReviewStep = modalStep === "review";
  const selectedCoinLabel =
    coinOptions.find((c) => c.key === selectedCoinKey)?.label ?? selectedCoinKey ?? "—";
  const walletLabel =
    WALLET_TYPES.find((w) => w.value === walletType)?.label ?? walletType;
  const subscribeTypeLabel =
    SUBSCRIBE_TYPES.find((t) => t.value === subscribeType)?.label ?? subscribeType;
  const planCurrency =
    selectedPlan?.currency ?? selectedPlan?.currency_short_name ?? "";
  const showZilConvert = isZilPlan(selectedPlan);
  const convertedZilDisplay =
    convertResult?.zil_amount ??
    convertResult?.zilAmount ??
    convertResult?.amount ??
    convertResult?.converted_amount;

  return (
    <main className="dashboard-main dashboard-subpage currency-pair-page">
      <section className="dashboard-hero currency-pair-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Staking</span>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>By admin</span>
          </p>
          <h1 className="dashboard-hero__title">Subscribe by admin</h1>
          <p className="dashboard-hero__subtitle">
            Subscribe a user to an earning package from{" "}
            <Link to="/dashboard/staking-packages">Staking Packages</Link>
          </p>
        </div>
        <div className="currency-pair-hero__actions">
          <button type="button" className="cp-btn cp-btn--primary cp-btn--add" onClick={openModal}>
          <i className="fas fa-plus" />Subscribe user
          </button>
        </div>
        <div className="dashboard-hero__icon currency-pair-hero__icon">
          <i className="fas fa-user-shield" />
        </div>
      </section>

      <div className="dashboard-body currency-pair-body">
        <section className="openorders-section">
          <div className="openorders-section__header">
            <div className="openorders-section__title-wrap">
              <span className="openorders-section__icon">
                <i className="fas fa-coins" />
              </span>
              <h2 className="openorders-section__title">Admin subscriptions</h2>
            </div>
            <div className="openorders-section__stats staking-stakes__header-actions">
              <span className="openorders-stat">
                <strong>{subscriptions.length}</strong> on page
                {totalCount != null && (
                  <>
                    {" "}
                    (<strong>{totalCount}</strong> total)
                  </>
                )}
              </span>
              {totalPages > 1 && (
                <span className="openorders-stat">
                  Page <strong>{page}</strong> / <strong>{totalPages}</strong>
                </span>
              )}
              <button
                type="button"
                className="staking-stakes__export-btn"
                onClick={handleExportExcel}
                disabled={exportLoading || listLoading}
                title="Download filtered subscriptions as Excel"
              >
                <i className={`fas ${exportLoading ? "fa-spinner fa-spin" : "fa-file-excel"}`} />
                {exportLoading ? "Exporting…" : "Download Excel"}
              </button>
            </div>
          </div>

          {listError && (
            <p style={{ color: "#dc2626", padding: "0 0.5rem 0.75rem", margin: 0 }}>{listError}</p>
          )}
          {exportMessage.text && (
            <p
              style={{
                color: exportMessage.type === "error" ? "#dc2626" : "#166534",
                padding: "0 0.5rem 0.75rem",
                margin: 0,
                background:
                  exportMessage.type === "error"
                    ? "rgba(220, 38, 38, 0.08)"
                    : "rgba(22, 101, 52, 0.08)",
                borderRadius: 8,
              }}
              role="status"
            >
              {exportMessage.text}
            </p>
          )}

          <div
            className="openorders-search-wrap"
            style={{ flexWrap: "wrap", gap: "0.5rem", alignItems: "flex-end" }}
          >
            <input
              type="text"
              placeholder="Filter by user (email / UUID / phone) — 1s debounce"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              className="openorders-search-input"
              style={{ minWidth: 220, flex: "1 1 240px" }}
            />
            <label style={{ display: "flex", flexDirection: "column", fontSize: 12, gap: 2 }}>
              From
              <input
                type="datetime-local"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="openorders-search-input"
                style={{ minWidth: 180, margin: 0 }}
              />
            </label>
            <label style={{ display: "flex", flexDirection: "column", fontSize: 12, gap: 2 }}>
              To
              <input
                type="datetime-local"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="openorders-search-input"
                style={{ minWidth: 180, margin: 0 }}
              />
            </label>
            <label className="staking-page-limit">
              <span>Rows per page</span>
              <select
                value={pageLimit}
                onChange={handlePageLimitChange}
                className="openorders-search-input staking-page-limit__select"
                disabled={listLoading}
              >
                {PAGE_LIMIT_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {listLoading ? (
            <div className="openorders-empty">
              <i className="fas fa-spinner fa-spin" /> Loading…
            </div>
          ) : subscriptions.length > 0 ? (
            <>
              <div className="openorders-table-wrap">
                <table className="openorders-table">
                  <thead>
                    <tr>
                      <th>UUID</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Plan</th>
                      <th>Term (days)</th>
                      <th>Invested</th>
                      <th>USDT value</th>
                      <th>Expected</th>
                      <th>Wallet</th>
                      <th>Status</th>
                      <th>Notes</th>
                      <th>Start</th>
                      <th>End</th>
                      <th>Refund / cancel</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscriptions.map((row) => {
                      const rid = row.id ?? row._id;
                      const uid = row.user?.id ?? row.user_id;
                      const u = row.user;
                      const uuidStr = u?.uuid ?? "—";
                      const plan = row.plan;
                      const termDays = row.duration_days ?? row.durationDays;
                      const invested =
                        row.invested_amount ?? row.investedAmount ?? row.amount;
                      const usdtValue = getUsdtValue(row);
                      const expected =
                        row.expected_return ?? row.expectedReturn ?? row.expected_reward;
                      const start = row.start_date ?? row.startDate;
                      const end = row.end_date ?? row.endDate;
                      const st = row.status ?? "—";
                      const cancelledAt = row.cancelled_at ?? row.cancelledAt;
                      const refundAmt = row.refund_amount ?? row.refundAmount;
                      const feePct = row.cancel_fee_percent ?? row.cancelFeePercent;
                      const stUp = String(st).toUpperCase();
                      const refundCell =
                        stUp === "CANCELLED"
                          ? [
                              refundAmt != null ? `Refund ${formatDec(refundAmt)}` : null,
                              feePct != null ? `Fee ${feePct}%` : null,
                              cancelledAt ? formatDate(cancelledAt) : null,
                            ]
                              .filter(Boolean)
                              .join(" · ") || "—"
                          : "—";
                      return (
                        <tr key={rid}>
                          <td>
                            {uid ? (
                              <Link
                                to={`/dashboard/trader/${uid}`}
                                state={{ trader: row.user }}
                                style={{
                                  color: "#dc2626",
                                  fontWeight: 600,
                                  textDecoration: "none",
                                }}
                              >
                                {uuidStr}
                              </Link>
                            ) : (
                              uuidStr
                            )}
                          </td>
                          <td>{userFullName(u)}</td>
                          <td>{u?.email ?? "—"}</td>
                          <td>{userPhone(u) ?? "—"}</td>
                          <td>
                            <strong title={plan?.id ?? plan?._id ?? row.plan_id}>
                              {planLabel(plan, row)}
                            </strong>
                          </td>
                          <td>{termDays != null ? String(termDays) : "—"}</td>
                          <td>{formatDec(invested)}</td>
                          <td>{formatDec(usdtValue)}</td>
                          <td>{formatDec(expected)}</td>
                          <td style={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}>{walletFlow(row)}</td>
                          <td>
                            <span
                              className={`cp-badge cp-badge--${statusBadgeClass(st)}`}
                              style={{ fontSize: "0.75rem" }}
                            >
                              {st}
                            </span>
                          </td>
                          <td
                            className="staking-stakes__notes-cell"
                            title={formatNotesForTable(row).title || undefined}
                          >
                            {formatNotesForTable(row).display}
                          </td>
                          <td>{formatDate(start)}</td>
                          <td>{formatDate(end)}</td>
                          <td style={{ fontSize: "0.78rem", maxWidth: 200 }}>{refundCell}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {totalCount > 0 && (
                <div
                  className="staking-stakes__pagination"
                  style={{ padding: "0.75rem 0", display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}
                >
                  <button
                    type="button"
                    className="cp-btn cp-btn--outline"
                    disabled={page <= 1 || listLoading}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </button>
                  <span className="openorders-stat" style={{ margin: 0 }}>
                    Showing {pageStart}–{pageEnd}
                    {totalCount != null ? ` of ${totalCount}` : ""}
                    {totalPages > 1 ? ` · Page ${page} of ${totalPages}` : ""}
                  </span>
                  <button
                    type="button"
                    className="cp-btn cp-btn--outline"
                    disabled={page >= totalPages || listLoading}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          ) : (
            <p className="openorders-empty">No subscriptions to show.</p>
          )}
        </section>
      </div>

      {modalOpen && (
        <div
          className="staking-subscribe-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="staking-admin-sub-title"
          onMouseDown={(ev) => {
            if (ev.target === ev.currentTarget) closeModal();
          }}
        >
          <div className="staking-subscribe-modal">
            <header className="staking-subscribe-modal__head">
              <div className="staking-subscribe-modal__head-main">
                <p className="staking-subscribe-modal__badge">
                  <i className="fas fa-shield-halved" aria-hidden />
                  Admin only
                </p>
                <h2 id="staking-admin-sub-title">
                  {isReviewStep ? "Verify subscription details" : "Subscribe user to plan"}
                </h2>
                <p className="staking-subscribe-modal__tagline">
                  {isReviewStep
                    ? "Review everything below. Edit if needed, then confirm to subscribe."
                    : "Select an active package, then identify the user by email, mobile number, or UUID."}
                </p>
              </div>
              <button
                type="button"
                className="staking-subscribe-modal__close"
                onClick={closeModal}
                aria-label="Close"
                disabled={submitLoading}
              >
                <i className="fas fa-times" />
              </button>
            </header>

            <div className="staking-subscribe-steps" aria-hidden>
              <div
                className={`staking-subscribe-step ${step1Done ? "staking-subscribe-step--done" : "staking-subscribe-step--active"}`}
              >
                <span className="staking-subscribe-step-num">
                  {step1Done ? <i className="fas fa-check" /> : "1"}
                </span>
                Asset
              </div>
              <div
                className={`staking-subscribe-step ${
                  !step1Done ? "" : step2Done ? "staking-subscribe-step--done" : "staking-subscribe-step--active"
                }`}
              >
                <span className="staking-subscribe-step-num">
                  {step2Done ? <i className="fas fa-check" /> : "2"}
                </span>
                Plan
              </div>
              <div
                className={`staking-subscribe-step ${
                  !step2Done ? "" : step3Done && !isReviewStep ? "staking-subscribe-step--active" : step3Done ? "staking-subscribe-step--done" : ""
                }`}
              >
                <span className="staking-subscribe-step-num">
                  {step3Done && !isReviewStep ? <i className="fas fa-check" /> : "3"}
                </span>
                User & pay
              </div>
              <div
                className={`staking-subscribe-step ${isReviewStep ? "staking-subscribe-step--active" : ""}`}
              >
                <span className="staking-subscribe-step-num">4</span>
                Verify
              </div>
            </div>

            <form onSubmit={isReviewStep ? (e) => e.preventDefault() : handleFormContinue}>
              <div className="staking-subscribe-modal__scroll">
                {isReviewStep ? (
                  <div className="staking-subscribe-review">
                    <p className="staking-subscribe-review__intro">
                      <i className="fas fa-clipboard-check" aria-hidden />
                      Please confirm these details are correct before subscribing the user.
                    </p>
                    <div className="staking-subscribe-review__grid">
                      <div className="staking-subscribe-review__card">
                        <h3>Package</h3>
                        <dl>
                          <div>
                            <dt>Asset</dt>
                            <dd>{selectedCoinLabel}</dd>
                          </div>
                          <div>
                            <dt>Plan</dt>
                            <dd>
                              {selectedPlan
                                ? `${planCurrency} · ${selectedPlan.duration_days ?? "—"}d · ${selectedPlan.return_percentage ?? "—"}%`
                                : "—"}
                            </dd>
                          </div>
                          <div>
                            <dt>Minimum stake</dt>
                            <dd>
                              {formatDec(selectedPlan?.min_amount)} {planCurrency}
                            </dd>
                          </div>
                          <div>
                            <dt>Plan ID</dt>
                            <dd className="staking-subscribe-review__mono">
                              {String(selectedPlanId)}
                            </dd>
                          </div>
                        </dl>
                      </div>
                      <div className="staking-subscribe-review__card">
                        <h3>User & payment</h3>
                        <dl>
                          <div>
                            <dt>User</dt>
                            <dd>
                              {selectedSubscribeUser ? (
                                <>
                                  {[selectedSubscribeUser.firstName, selectedSubscribeUser.lastName]
                                    .filter(Boolean)
                                    .join(" ") || selectedSubscribeUser.label}
                                  {selectedSubscribeUser.emailId ? (
                                    <span className="staking-subscribe-review__sub">
                                      {selectedSubscribeUser.emailId}
                                      {selectedSubscribeUser.uuid
                                        ? ` · ${selectedSubscribeUser.uuid}`
                                        : ""}
                                    </span>
                                  ) : selectedSubscribeUser.uuid ? (
                                    <span className="staking-subscribe-review__sub">
                                      {selectedSubscribeUser.uuid}
                                    </span>
                                  ) : null}
                                  {selectedSubscribeUser.displayPhone ||
                                  selectedSubscribeUser.mobileNumber ? (
                                    <span className="staking-subscribe-review__sub">
                                      {selectedSubscribeUser.displayPhone ||
                                        selectedSubscribeUser.mobileNumber}
                                    </span>
                                  ) : null}
                                </>
                              ) : (
                                emailId.trim()
                              )}
                            </dd>
                          </div>
                          <div>
                            <dt>Invest amount</dt>
                            <dd className="staking-subscribe-review__highlight">
                              {formatPlain(sanitizeAmount(investAmount))} {planCurrency}
                            </dd>
                          </div>
                          <div>
                            <dt>Debit wallet</dt>
                            <dd>{walletLabel}</dd>
                          </div>
                          <div>
                            <dt>Subscription type</dt>
                            <dd>{subscribeTypeLabel}</dd>
                          </div>
                          {notes.trim() ? (
                            <div>
                              <dt>Notes</dt>
                              <dd>{notes.trim()}</dd>
                            </div>
                          ) : null}
                          {showZilConvert && usdtConvertAmount.trim() ? (
                            <div>
                              <dt>USDT converted</dt>
                              <dd>
                                {formatDec(usdtConvertAmount.trim())} USDT
                                {convertedZilDisplay != null && convertedZilDisplay !== ""
                                  ? ` → ${formatPlain(convertedZilDisplay)} ZILN`
                                  : ""}
                              </dd>
                            </div>
                          ) : null}
                        </dl>
                      </div>
                    </div>
                    {submitError ? (
                      <div className="staking-subscribe-alert" role="alert">
                        <i className="fas fa-circle-exclamation" style={{ marginTop: "0.1rem" }} />
                        <span>{submitError}</span>
                      </div>
                    ) : null}
                  </div>
                ) : packagesLoading ? (
                  <div className="staking-subscribe-loading">
                    <div className="staking-subscribe-loading__orbit" aria-hidden />
                    <p>Loading active staking packages…</p>
                  </div>
                ) : (
                  <div className="staking-subscribe-modal__layout">
                    <div className="staking-subscribe-modal__fields">
                      <label className="staking-subscribe-field">
                        <span className="staking-subscribe-field__label">
                          <i className="fas fa-coins" aria-hidden />
                          Coin / currency
                        </span>
                        <select
                          className="staking-subscribe-field__control"
                          value={selectedCoinKey}
                          onChange={(e) => setSelectedCoinKey(e.target.value)}
                        >
                          <option value="">Select asset…</option>
                          {coinOptions.map((c) => (
                            <option key={c.key} value={c.key}>
                              {c.label}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="staking-subscribe-field">
                        <span className="staking-subscribe-field__label">
                          <i className="fas fa-layer-group" aria-hidden />
                          Staking plan
                          <span style={{ opacity: 0.75, fontWeight: 600 }}>
                            ({plansForCoin.length} active)
                          </span>
                        </span>
                        <select
                          className="staking-subscribe-field__control"
                          value={selectedPlanId}
                          onChange={(e) => setSelectedPlanId(e.target.value)}
                          disabled={!selectedCoinKey}
                        >
                          <option value="">Select plan…</option>
                          {plansForCoin.map((p) => {
                            const id = pkgId(p);
                            const label = `${p.currency ?? p.currency_short_name ?? ""} · ${p.duration_days ?? "—"}d · ${p.return_percentage ?? "—"}% (min ${formatDec(p.min_amount)})`;
                            return (
                              <option key={id} value={String(id)}>
                                {label}
                              </option>
                            );
                          })}
                        </select>
                      </label>

                      {minHint ? <p className="staking-subscribe-field__hint">{minHint}</p> : null}

                      <div
                        className="staking-subscribe-field staking-subscribe-field--user-search"
                        ref={userSearchWrapRef}
                      >
                        <span className="staking-subscribe-field__label">
                          <i className="fas fa-user" aria-hidden />
                          User (email / phone / UUID)
                        </span>
                        <input
                          type="search"
                          autoComplete="off"
                          className="staking-subscribe-field__control"
                          placeholder="Type email, mobile, or UUID…"
                          value={emailId}
                          onChange={(e) => handleUserInputChange(e.target.value)}
                          onFocus={() => {
                            if (emailId.trim().length >= USER_SEARCH_MIN_CHARS) {
                              setShowUserSuggestions(true);
                            }
                          }}
                        />
                        {showUserSuggestions && emailId.trim().length >= USER_SEARCH_MIN_CHARS && (
                          <ul className="staking-subscribe-user-suggestions" role="listbox">
                            {userSearchLoading ? (
                              <li className="staking-subscribe-user-suggest-item staking-subscribe-user-suggest-item--muted">
                                <span>Searching users…</span>
                              </li>
                            ) : userSearchResults.length > 0 ? (
                              userSearchResults.map((user) => {
                                const uid = user.userId || user._id || user.id;
                                const name = [user.firstName, user.lastName]
                                  .filter(Boolean)
                                  .join(" ");
                                return (
                                  <li key={uid || user.label}>
                                    <button
                                      type="button"
                                      className="staking-subscribe-user-suggest-item"
                                      role="option"
                                      onMouseDown={(e) => e.preventDefault()}
                                      onClick={() => selectSubscribeUser(user)}
                                    >
                                      {name ? (
                                        <span className="staking-subscribe-user-suggest__name">
                                          {name}
                                        </span>
                                      ) : null}
                                      {user.emailId ? (
                                        <span className="staking-subscribe-user-suggest__line">
                                          {user.emailId}
                                          {user.uuid ? (
                                            <span className="staking-subscribe-user-suggest__uuid">
                                              {" "}
                                              · {user.uuid}
                                            </span>
                                          ) : null}
                                        </span>
                                      ) : user.uuid ? (
                                        <span className="staking-subscribe-user-suggest__line">
                                          {user.uuid}
                                        </span>
                                      ) : null}
                                      {user.displayPhone || user.mobileNumber ? (
                                        <span className="staking-subscribe-user-suggest__meta">
                                          {user.displayPhone || user.mobileNumber}
                                        </span>
                                      ) : null}
                                    </button>
                                  </li>
                                );
                              })
                            ) : !userSearchError ? (
                              <li className="staking-subscribe-user-suggest-item staking-subscribe-user-suggest-item--muted">
                                <span>No matching users</span>
                              </li>
                            ) : null}
                            {userSearchError ? (
                              <li className="staking-subscribe-user-suggest-item staking-subscribe-user-suggest-item--error">
                                <span>{userSearchError}</span>
                              </li>
                            ) : null}
                          </ul>
                        )}
                        {selectedSubscribeUser ? (
                          <p className="staking-subscribe-field__hint staking-subscribe-field__hint--selected">
                            <i className="fas fa-check-circle" aria-hidden /> Selected:{" "}
                            {selectedSubscribeUser.label ||
                              subscribeUserIdentifier(selectedSubscribeUser)}
                          </p>
                        ) : (
                          <span className="staking-subscribe-field__hint">
                            Type at least {USER_SEARCH_MIN_CHARS} characters — pick a user from
                            suggestions or enter identifier manually.
                          </span>
                        )}
                      </div>

                      {(stakeCheckLoading || stakeCheckResult) && (
                        <div className={`staking-subscribe-stake-check ${stakeCheckResult?.already_staked ? "staking-subscribe-stake-check--warn" : stakeCheckResult ? "staking-subscribe-stake-check--ok" : "staking-subscribe-stake-check--loading"}`}>
                          {stakeCheckLoading ? (
                            <p className="staking-subscribe-stake-check__loading">
                              <i className="fas fa-spinner fa-spin" /> Checking existing stake…
                            </p>
                          ) : stakeCheckResult?.already_staked ? (
                            <>
                              <div className="staking-subscribe-stake-check__head">
                                <i className="fas fa-exclamation-triangle" />
                                <strong>User already has an active stake on this plan</strong>
                              </div>
                              {stakeCheckResult.data && (
                                <div className="staking-subscribe-stake-check__details">
                                  <span>
                                    <span className="staking-subscribe-stake-check__key">Invested</span>
                                    <span className="staking-subscribe-stake-check__val">{formatDec(stakeCheckResult.data.invested_amount)} {stakeCheckResult.data.currency}</span>
                                  </span>
                                  <span>
                                    <span className="staking-subscribe-stake-check__key">Status</span>
                                    <span className="staking-subscribe-stake-check__val">{stakeCheckResult.data.status}</span>
                                  </span>
                                  <span>
                                    <span className="staking-subscribe-stake-check__key">Paid days</span>
                                    <span className="staking-subscribe-stake-check__val">{stakeCheckResult.data.paid_days ?? "—"} / {stakeCheckResult.data.duration_days ?? "—"}d</span>
                                  </span>
                                  <span>
                                    <span className="staking-subscribe-stake-check__key">Ends</span>
                                    <span className="staking-subscribe-stake-check__val">{formatDate(stakeCheckResult.data.end_date)}</span>
                                  </span>
                                </div>
                              )}
                              <p className="staking-subscribe-stake-check__note">
                                You can still proceed — admin will be asked to confirm reinvestment.
                              </p>
                            </>
                          ) : stakeCheckResult && !stakeCheckResult.already_staked ? (
                            <p className="staking-subscribe-stake-check__ok-msg">
                              <i className="fas fa-check-circle" /> No existing stake found on this plan. Good to go.
                            </p>
                          ) : null}
                        </div>
                      )}

                      <label className="staking-subscribe-field">
                        <span className="staking-subscribe-field__label">
                          <i className="fas fa-coins" aria-hidden />
                          Invest amount{showZilConvert ? " (ZILN)" : ""}
                        </span>
                        <input
                          type="text"
                          inputMode="decimal"
                          className="staking-subscribe-field__control"
                          placeholder={showZilConvert ? "ZILN amount" : "e.g. 100"}
                          value={investAmount}
                          onChange={(e) => setInvestAmount(e.target.value)}
                        />
                        {showZilConvert ? (
                          <span className="staking-subscribe-field__hint">
                            ZILN package — enter ZILN amount or use the USDT converter on the right.
                          </span>
                        ) : null}
                      </label>

                      <label className="staking-subscribe-field">
                        <span className="staking-subscribe-field__label">
                          <i className="fas fa-wallet" aria-hidden />
                          Debit wallet
                        </span>
                        <select
                          className="staking-subscribe-field__control"
                          value={walletType}
                          onChange={(e) => setWalletType(e.target.value)}
                          title={isStakingWallet(walletType) ? STAKING_WALLET_HINT : undefined}
                        >
                          {WALLET_TYPES.map((w) => (
                            <option key={w.value} value={w.value}>
                              {w.label}
                            </option>
                          ))}
                        </select>
                        {isStakingWallet(walletType) ? (
                          <span className="staking-subscribe-field__hint">
                            {STAKING_WALLET_HINT}
                          </span>
                        ) : null}
                      </label>

                      <label className="staking-subscribe-field">
                        <span className="staking-subscribe-field__label">
                          <i className="fas fa-sliders" aria-hidden />
                          Subscription type
                        </span>
                        <select
                          className="staking-subscribe-field__control"
                          value={subscribeType}
                          onChange={(e) => setSubscribeType(e.target.value)}
                        >
                          {SUBSCRIBE_TYPES.map((t) => (
                            <option key={t.value} value={t.value}>
                              {t.label}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="staking-subscribe-field">
                        <span className="staking-subscribe-field__label">
                          <i className="fas fa-note-sticky" aria-hidden />
                          Notes <span style={{ opacity: 0.65, fontWeight: 600 }}>(optional)</span>
                        </span>
                        <textarea
                          className="staking-subscribe-field__control"
                          placeholder="Add an optional note for this subscription…"
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          rows={3}
                          style={{ resize: "vertical" }}
                        />
                      </label>

                      {submitError ? (
                        <div className="staking-subscribe-alert" role="alert">
                          <i className="fas fa-circle-exclamation" style={{ marginTop: "0.1rem" }} />
                          <span>{submitError}</span>
                        </div>
                      ) : null}
                    </div>

                    {selectedPlan ? (
                      <aside className="staking-subscribe-summary">
                        <h3 className="staking-subscribe-summary__title">Selected package</h3>
                        <div className="staking-subscribe-summary__coin">
                          <div className="staking-subscribe-summary__coin-icon">
                            {planIconSrc ? (
                              <img
                                src={planIconSrc}
                                alt=""
                                style={{ width: 28, height: 28, objectFit: "contain" }}
                              />
                            ) : (
                              <i className="fas fa-coins" aria-hidden />
                            )}
                          </div>
                          <div className="staking-subscribe-summary__coin-text">
                            <strong>{selectedPlan.currency ?? selectedPlan.currency_short_name ?? "—"}</strong>
                            <span>{selectedPlan.currency_fullname ?? "Earning plan"}</span>
                          </div>
                        </div>
                        <dl className="staking-subscribe-summary__dl">
                          <div>
                            <dt>Duration</dt>
                            <dd>{selectedPlan.duration_days != null ? `${selectedPlan.duration_days} days` : "—"}</dd>
                          </div>
                          <div>
                            <dt>Return</dt>
                            <dd>
                              {selectedPlan.return_percentage != null
                                ? `${selectedPlan.return_percentage}%`
                                : "—"}
                            </dd>
                          </div>
                          <div>
                            <dt>Minimum</dt>
                            <dd>
                              {formatDec(selectedPlan.min_amount)}{" "}
                              {selectedPlan.currency ?? selectedPlan.currency_short_name ?? ""}
                            </dd>
                          </div>
                          <div>
                            <dt>Plan ID</dt>
                            <dd style={{ fontSize: "0.72rem", wordBreak: "break-all", opacity: 0.9 }}>
                              {String(pkgId(selectedPlan) ?? "—")}
                            </dd>
                          </div>
                        </dl>

                        {showZilConvert ? (
                          <div className="staking-subscribe-zil-convert">
                            <h4 className="staking-subscribe-zil-convert__title">
                              <i className="fas fa-exchange-alt" aria-hidden /> USDT → ZILN
                            </h4>
                            <p className="staking-subscribe-zil-convert__sub">
                              Enter USDT value to calculate ZILN stake amount at configured admin rates.
                            </p>
                            <label className="staking-subscribe-zil-convert__field">
                              <span>USDT amount</span>
                              <input
                                type="text"
                                inputMode="decimal"
                                placeholder="e.g. 100"
                                value={usdtConvertAmount}
                                onChange={(e) => {
                                  setUsdtConvertAmount(e.target.value);
                                  setConvertError("");
                                }}
                                disabled={convertLoading}
                              />
                            </label>
                            <button
                              type="button"
                              className="staking-subscribe-zil-convert__btn"
                              onClick={handleZilConvert}
                              disabled={convertLoading || !usdtConvertAmount.trim()}
                            >
                              {convertLoading ? (
                                <>
                                  <i className="fas fa-spinner fa-spin" aria-hidden /> Converting…
                                </>
                              ) : (
                                <>
                                  <i className="fas fa-calculator" aria-hidden /> Convert to ZILN
                                </>
                              )}
                            </button>
                            {convertError ? (
                              <p className="staking-subscribe-zil-convert__error" role="alert">
                                {convertError}
                              </p>
                            ) : null}
                            {convertedZilDisplay != null && convertedZilDisplay !== "" ? (
                              <div className="staking-subscribe-zil-convert__result">
                                <span className="staking-subscribe-zil-convert__result-label">
                                  ZILN amount
                                </span>
                                <strong>{formatPlain(convertedZilDisplay)} ZILN</strong>
                                {convertResult?.usdt_amount != null ? (
                                  <span className="staking-subscribe-zil-convert__result-meta">
                                    from {formatDec(convertResult.usdt_amount)} USDT
                                  </span>
                                ) : null}
                                <button
                                  type="button"
                                  className="staking-subscribe-zil-convert__apply"
                                  onClick={applyConvertedZilAmount}
                                >
                                  Use as invest amount
                                </button>
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                      </aside>
                    ) : (
                      <aside className="staking-subscribe-summary staking-subscribe-summary--empty">
                        <h3 className="staking-subscribe-summary__title">Selected package</h3>
                        <div className="staking-subscribe-summary__coin">
                          <div className="staking-subscribe-summary__coin-icon">
                            <i className="fas fa-compass" aria-hidden />
                          </div>
                          <div className="staking-subscribe-summary__coin-text">
                            <strong>Choose a plan</strong>
                            <span>Select coin, then a plan to see live terms here.</span>
                          </div>
                        </div>
                      </aside>
                    )}
                  </div>
                )}
              </div>

              <footer className="staking-subscribe-modal__foot">
                {isReviewStep ? (
                  <>
                    <button
                      type="button"
                      className="staking-subscribe-btn-ghost"
                      onClick={backToEditForm}
                      disabled={submitLoading}
                    >
                      <i className="fas fa-pen" aria-hidden /> Edit details
                    </button>
                    <button
                      type="button"
                      className="staking-subscribe-btn-primary"
                      onClick={handleConfirmSubscribe}
                      disabled={submitLoading}
                    >
                      {submitLoading ? (
                        <i className="fas fa-spinner fa-spin" aria-hidden />
                      ) : (
                        <i className="fas fa-check" aria-hidden />
                      )}
                      {submitLoading ? "Subscribing…" : "Confirm & subscribe"}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="staking-subscribe-btn-ghost"
                      onClick={closeModal}
                      disabled={submitLoading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="staking-subscribe-btn-primary"
                      disabled={submitLoading || packagesLoading || !packages.length}
                    >
                      <i className="fas fa-arrow-right" aria-hidden />
                      Review & continue
                    </button>
                  </>
                )}
              </footer>
            </form>
          </div>
        </div>
      )}
      {reinvestConfirmOpen && (
        <div
          className="staking-payout-overlay"
          style={{ zIndex: 1080 }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="reinvest-confirm-title"
          onClick={() => setReinvestConfirmOpen(false)}
        >
          <div className="staking-reinvest-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="staking-reinvest-confirm__head">
              <i className="fas fa-rotate" aria-hidden />
              <h2 id="reinvest-confirm-title">Reinvest on same plan?</h2>
            </div>
            <div className="staking-reinvest-confirm__body">
              <p className="staking-reinvest-confirm__msg">
                This user already has an <strong>active stake</strong> on the selected plan.
              </p>
              {stakeCheckResult?.data && (
                <div className="staking-reinvest-confirm__existing">
                  <div>
                    <span>Invested</span>
                    <strong>{formatDec(stakeCheckResult.data.invested_amount)} {stakeCheckResult.data.currency}</strong>
                  </div>
                  <div>
                    <span>Status</span>
                    <strong>{stakeCheckResult.data.status}</strong>
                  </div>
                  <div>
                    <span>Progress</span>
                    <strong>{stakeCheckResult.data.paid_days ?? "—"} / {stakeCheckResult.data.duration_days ?? "—"} days</strong>
                  </div>
                  <div>
                    <span>Ends</span>
                    <strong>{formatDate(stakeCheckResult.data.end_date)}</strong>
                  </div>
                </div>
              )}
              <p className="staking-reinvest-confirm__question">
                Do you want to create an additional (reinvestment) subscription anyway?
              </p>
            </div>
            <div className="staking-reinvest-confirm__foot">
              <button
                type="button"
                className="staking-subscribe-btn-ghost"
                onClick={() => setReinvestConfirmOpen(false)}
              >
                <i className="fas fa-times" /> No, go back
              </button>
              <button
                type="button"
                className="staking-subscribe-btn-primary"
                onClick={handleReinvestConfirm}
              >
                <i className="fas fa-rotate" /> Yes, reinvest
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default StakingSubscribeByAdminPage;
